import pluralize from "pluralize"
import * as util from "lib/util"
import * as date from "lib/date"
import { CloseOutlined, CalendarOutlined } from "@ant-design/icons"
import { motion } from "framer-motion"
import { InputNumber, Button, Typography, Space, Tooltip, ConfigProvider } from "antd"
import { Dropdown } from "components"
import { ComponentProps, useRef, useState, useMemo, memo, useCallback } from "react"
import * as hooks from "lib/hooks"
import * as dateFilter from "enums/dateFilter"
import * as timePeriod from "enums/timePeriod"
import { DateFilter } from "enums/dateFilter"
import { TimePeriodTag } from "enums/timePeriod"
import { DatePicker } from "antd"
import type { Dayjs } from "dayjs"
import { ColorTheme, MaterialColors } from "lib/hooks"

/* eslint-disable-next-line */
const { Text } = Typography
/* eslint-disable-next-line */
const { RangePicker } = DatePicker

// Custom styled components using CSS-in-JS
/* eslint-disable-next-line */
const StyledBox = ({ sx, component, ...props }: any) => {
	/* eslint-disable-next-line */
	const Component = component || "div"
	return <Component style={sx} {...props} />
}

/* eslint-disable-next-line */
const StyledStack = ({ direction = "horizontal", spacing = 8, sx, children, ...props }: any) => (
	<div
		style={{
			display: "flex",
			flexDirection: direction === "horizontal" ? "row" : "column",
			gap: spacing * 8,
			alignItems: "center",
			width: "100%",
			...sx,
		}}
		{...props}
	>
		{children}
	</div>
)


//keep these styles for ant in this file
const baseInputStyle = (colors: ColorTheme) => ({
	width: 60,
	flexShrink: 0,
	fontSize: "0.875rem",
	fontWeight: 500,
	color: colors.text,
	textAlign: "center",
	padding: 0,
	height: "30px",
	background: alpha(colors.background, 0.3),
	margin: "0 8px",
	borderRadius: "12px",
	border: `1px solid ${alpha(colors.border, 0.3)}`,
	transition: "all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)",
	outline: "none",
});

const dropdownSx = {
	width: "100%",
	minWidth: 0,
	".ant-select": { width: "100%", height: "100%" },
	".ant-select-selector": {
		border: "none !important",
		backgroundColor: "transparent !important",
		padding: "0 12px !important",
		height: "100% !important",
		display: "flex",
		alignItems: "center",
		minHeight: "32px",
	},
	".ant-select-selection-search": { width: "100%" },
	".ant-select-selection-search-input": {
		width: "100% !important",
		height: "100% !important",
	},
	".ant-select-selection-item": {
		fontSize: "0.875rem",
		fontWeight: 500,
		letterSpacing: "0.01em",
		lineHeight: "normal",
		padding: 0,
		textOverflow: "ellipsis",
		overflow: "hidden",
		whiteSpace: "nowrap",
		maxWidth: "100%",
	},
	".ant-select-arrow": {
		color: "inherit",
		top: "50%",
		transform: "translateY(-50%)",
		flexShrink: 0,
	},
};

const clearButtonStyle = {
	padding: "8px",
	flexShrink: 0,
	marginLeft: "8px",
	minWidth: "36px",
	width: "36px",
	height: "36px",
	border: "none",
	borderRadius: "50%",
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	transition: "all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)",
};

export interface DateFilterProps {
	value: DateFilter
	onChange: (value: DateFilter) => void
	disableRelative?: boolean
	disableAllTime?: boolean
	disableCustom?: boolean
	allowSingleDateOnly?: boolean
	onOpenCalendar?: () => void
	onCloseCalendar?: () => void
	relativeUnits?: TimePeriodTag[]
	inputProps?: Partial<ComponentProps<typeof InputNumber>>
	format?: string
	label?: string
}

interface dropdownOption {
	id: string
	name: string
	type: "relative" | "allTime" | "custom"
}

interface memoizedTextFieldProps {
	value: string
	handleTextChange: (value: string) => void
	inputProps: any
	colors: ColorTheme
}

// Memoized inner components to prevent unnecessary re-renders
/* eslint-disable-next-line */
const MemoizedTextField = memo<memoizedTextFieldProps>(
	({ value, handleTextChange, inputProps, colors }) => (
		<ConfigProvider
			theme={{
				components: {
					InputNumber: {
						activeBorderColor: "transparent",
						hoverBorderColor: "transparent",
						borderRadius: 0,
					},
				},
			}}
		>
			<InputNumber
				value={value}
				onChange={(val: number | null) => {
					handleTextChange(val !== null ? String(val) : "")
				}}
				min={1}
				placeholder="1"
				variant="borderless"
				controls={false}
				style={baseInputStyle(colors)}
				onMouseEnter={(e) => {
					const target = e.currentTarget as HTMLElement
					target.style.borderColor = alpha(colors.borderHover, 0.5)
					target.style.backgroundColor = alpha(colors.background, 0.5)
				}}
				onMouseLeave={(e) => {
					const target = e.currentTarget as HTMLElement
					if (document.activeElement !== target.querySelector("input")) {
						target.style.borderColor = alpha(colors.border, 0.3)
						target.style.backgroundColor = alpha(colors.background, 0.3)
					}
				}}
				onFocus={(e) => {
					const target = e.currentTarget.parentElement as HTMLElement
					target.style.borderColor = colors.borderFocus
					target.style.backgroundColor = alpha(colors.background, 0.6)
					target.style.boxShadow = `0 0 0 2px ${alpha(colors.borderFocus, 0.15)}`
				}}
				onBlur={(e) => {
					const target = e.currentTarget.parentElement as HTMLElement
					target.style.borderColor = alpha(colors.border, 0.3)
					target.style.backgroundColor = alpha(colors.background, 0.3)
					target.style.boxShadow = "none"
				}}
				{...inputProps}
			/>
		</ConfigProvider>
	)
)

interface memoizedDropdownContainerProps {
	dropdownData: dropdownOption[]
	currentDropdownValue: dropdownOption | null
	handleDropdownChange: (selected: dropdownOption | null) => void
}

// Memoized dropdown container - FIXED STYLING
/* eslint-disable-next-line */
const MemoizedDropdownContainer = memo<memoizedDropdownContainerProps>(
	({ dropdownData, currentDropdownValue, handleDropdownChange }) => (
		<div
			style={{
				flex: "0 1 auto",
				minWidth: "120px",
				maxWidth: "200px",
				display: "flex",
				alignItems: "center",
			}}
		>
			<Dropdown
				data={dropdownData}
				value={currentDropdownValue}
				onChange={handleDropdownChange}
				clearable={false}
				getOptionLabel={(option: dropdownOption) => option.name}
				isOptionEqualToValue={(option: dropdownOption, value: dropdownOption) =>
					option.id === value.id
				}
				multiple={false}
				sx={dropdownSx}
				textFieldProps={{
					style: {
						fontSize: "0.875rem",
						fontWeight: 500,
						letterSpacing: "0.01em",
						width: "100%",
					},
				}}
			/>
		</div>
	)
)

interface memoizedRangePickerProps {
	datePickerValue: [Dayjs | null, Dayjs | null]
	calendarOpen: boolean
	isCustom: boolean
	setCalendarOpen: (open: boolean) => void
	onOpenCalendar?: () => void
	onCloseCalendar?: () => void
	onChange: (value: DateFilter) => void
	materialColors: MaterialColors
	format?: string
}

/* eslint-disable-next-line */
const MemoizedRangePicker = memo<memoizedRangePickerProps>(
	({
		datePickerValue,
		calendarOpen,
		isCustom,
		setCalendarOpen,
		onOpenCalendar,
		onCloseCalendar,
		onChange,
		materialColors,
		format,
	}) => (
		<ConfigProvider
			theme={{
				token: {
					colorPrimary: materialColors.primary,
					colorPrimaryHover: materialColors.primaryHover || materialColors.primary,
					colorBgContainer: materialColors.surfaceContainer,
					colorBgElevated: materialColors.surfaceContainerHigh,
					colorText: materialColors.onSurface,
					colorTextDisabled: `rgba(${materialColors.onSurfaceVariant}, 0.38)`,
					colorBorder: `rgba(${materialColors.outline}, 0.12)`,
					borderRadius: 20,
					fontSize: 14,
					fontFamily: "'Google Sans', 'Roboto', sans-serif",
					controlHeight: 40,
					lineHeight: 1.5,
				},
				components: {
					DatePicker: {
						cellHoverBg: `rgba(${materialColors.onSurface}, 0.04)`,
						cellActiveWithRangeBg: `rgba(${materialColors.primary}, 0.12)`,
						cellRangeBorderColor: "transparent",
						cellBgDisabled: "transparent",
						cellHeight: 40,
						cellWidth: 40,
						textHeight: 40,
						controlItemBgActive: materialColors.primary,
						controlItemBgHover: `rgba(${materialColors.onSurface}, 0.04)`,
					},
				},
			}}
			popupMatchSelectWidth={false}
		>
			<RangePicker
				value={datePickerValue}
				onClick={() => setCalendarOpen(true)}
				onOpenChange={(isOpen) => {
					setCalendarOpen(isOpen)
					if (isOpen && onOpenCalendar) {
						onOpenCalendar()
					} else if (!isOpen && onCloseCalendar) {
						onCloseCalendar()
					}
				}}
				open={calendarOpen && isCustom}
				autoFocus
				onChange={(dateRange) => {
					if (dateRange) {
						const d1 = dateRange?.First().Expect("d1 should exist")
						const d2 = dateRange.Second().Expect("d2 should exist")
						if (d1 && d2) {
							onChange(dateFilter.Range([d1, d2]))
						} else {
							onChange(dateFilter.AllTime())
						}
					} else {
						onChange(dateFilter.AllTime())
					}
				}}
				style={{
					width: "100%",
					padding: "0 16px",
					boxSizing: "border-box",
					border: "none",
					borderRadius: "20px",
					backgroundColor: "transparent",
					boxShadow: "none",
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					cursor: "pointer",
					transition: "all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)",
					color: materialColors.isDark
						? materialColors.surfaceDark?.onSurface
						: materialColors.surfaceLight?.onSurface,
					fontSize: "0.875rem",
					fontWeight: 500,
					letterSpacing: "0.01em",
					overflow: "hidden",
					whiteSpace: "nowrap",
					textOverflow: "ellipsis",
					minWidth: 0,
					flex: "1 1 auto",
				}}
				popupStyle={{
					["--surface-container" as any]: materialColors.surfaceContainer,
					["--on-surface" as any]: materialColors.onSurface,
					["--on-surface-variant" as any]: materialColors.onSurfaceVariant,
					["--primary" as any]: materialColors.primary,
					["--primary-hover" as any]:
						materialColors.primaryHover || materialColors.primary,
					["--primary-container" as any]: `rgba(${materialColors.primary}, 0.12)`,
					["--on-primary" as any]: materialColors.onPrimary || "#ffffff",
					["--outline" as any]: materialColors.outline,
					["--outline-variant" as any]: `rgba(${materialColors.outline}, 0.12)`,
					["--state-hover" as any]: `rgba(${materialColors.onSurface}, 0.04)`,
					["--state-pressed" as any]: `rgba(${materialColors.onSurface}, 0.08)`,
					["--state-disabled" as any]: `rgba(${materialColors.onSurface}, 0.38)`,
					["--surface-container-low" as any]: `rgba(${materialColors.surfaceContainer}, ${materialColors.isDark ? 0.04 : 0.02
						})`,
				}}
				suffixIcon={null}
				separator={
					<Text
						style={{
							margin: "0 8px",
							color: materialColors.onSurfaceVariant,
							fontSize: "0.875rem",
							flexShrink: 0,
						}}
					>
						–
					</Text>
				}
				format={format || "MMM D, YYYY"}
				allowClear={true}
				clearIcon={
					<CloseOutlined
						style={{
							fontSize: 16,
							color: materialColors.onSurfaceVariant,
							transition: "all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)",
							flexShrink: 0,
						}}
					/>
				}
				bordered={false}
				className="custom-range-picker"
				panelRender={(panelNode) => (
					<motion.div
						initial={{ opacity: 0, y: -8, scale: 0.96 }}
						animate={{ opacity: 1, y: 0, scale: 1 }}
						exit={{ opacity: 0, y: -8, scale: 0.96 }}
						transition={{
							duration: 0.25,
							ease: [0.0, 0.0, 0.2, 1.0],
						}}
						style={{
							borderRadius: "28px",
							overflow: "hidden",
							boxShadow:
								"0 8px 32px rgba(0, 0, 0, 0.12), 0 4px 16px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.04)",
							backgroundColor: materialColors.surfaceContainer,
						}}
					>
						{panelNode}
					</motion.div>
				)}
				renderExtraFooter={() => (
					<StyledBox
						sx={{
							display: "flex",
							justifyContent: "flex-end",
							gap: 8,
							padding: 16,
							borderTop: `1px solid rgba(${materialColors.outline}, 0.12)`,
							backgroundColor: `rgba(${materialColors.surfaceContainer}, ${materialColors.isDark ? 0.04 : 0.02
								})`,
						}}
					>
						<motion.button
							whileHover={{ scale: 1.02 }}
							whileTap={{ scale: 0.98 }}
							style={{
								height: "40px",
								padding: "0 24px",
								borderRadius: "20px",
								fontFamily: "'Google Sans', 'Roboto', sans-serif",
								fontSize: "14px",
								fontWeight: 500,
								letterSpacing: "0.01em",
								transition: "all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)",
								border: "none",
								backgroundColor: "transparent",
								color: materialColors.primary,
								cursor: "pointer",
							}}
							onMouseEnter={(e) => {
								(e.target as HTMLButtonElement).style.backgroundColor =
									`rgba(${materialColors.primary}, 0.08)`
							}}
							onMouseLeave={(e) => {
								(e.target as HTMLButtonElement).style.backgroundColor =
									"transparent"
							}}
							onClick={() => setCalendarOpen(false)}
						>
							Cancel
						</motion.button>
					</StyledBox>
				)}
			/>
			<style>{`
				.custom-range-picker .ant-picker-input {
					flex: 1 1 auto;
					min-width: 0;
					overflow: hidden;
				}
				.custom-range-picker .ant-picker-input > input {
					text-overflow: ellipsis;
					overflow: hidden;
					white-space: nowrap;
					min-width: 0;
					flex: 1 1 auto;
				}
				.custom-range-picker .ant-picker-range-separator {
					flex-shrink: 0;
					padding: 0 4px;
					min-width: auto;
				}
				.custom-range-picker .ant-picker-active-bar {
					display: none;
				}
				.custom-range-picker .ant-picker-clear {
					flex-shrink: 0;
					margin-left: 4px;
				}
				.custom-range-picker .ant-picker-suffix {
					flex-shrink: 0;
				}
			`}</style>
		</ConfigProvider>
	)
)

// Helper function to create rgba colors
const alpha = (color: string, opacity: number) => {
	// If color is already rgba, extract RGB and apply new opacity
	if (color.HasPrefix("rgba")) {
		const rgbMatch = color.match!(/rgba?\((\d+),\s*(\d+),\s*(\d+)/)
		if (rgbMatch) {
			return `rgba(${rgbMatch[1]}, ${rgbMatch[2]}, ${rgbMatch[3]}, ${opacity})`
		}
	}
	// If hex color, convert to rgba
	if (color.HasPrefix("#")) {
		const hex = color.Replace("#", "")
		const r = parseInt(hex.substring(0, 2), 16)
		const g = parseInt(hex.substring(2, 4), 16)
		const b = parseInt(hex.substring(4, 6), 16)
		return `rgba(${r}, ${g}, ${b}, ${opacity})`
	}
	// Fallback
	return color
}

function dateFilterComponent({
	onChange,
	value: df,
	disableCustom,
	disableAllTime,
	format,
	onOpenCalendar,
	label,
	onCloseCalendar,
	inputProps = {},
	relativeUnits = timePeriod.TimePeriodTags,
}: DateFilterProps) {
	const containerRef = useRef<HTMLDivElement>(null)
	const [calendarOpen, setCalendarOpen] = useState(false)
	const materialColors = hooks.UseMaterialColors()

	// Memoize the value for the TextField to prevent re-renders
	const textFieldValue = useMemo(
		() =>
			df.Match({
				Relative: ({ count }) => (count === 0 ? "" : count.String()),
				_: () => "1",
			}),
		[df]
	)

	// Handle user typing in the "Last ____" text field
	const handleTextChange = (value: string) => {
		if (!value || value.TrimSpace().length === 0) {
			df.Match({
				Relative: ({ unit }) => {
					onChange(dateFilter.Relative({ unit, count: 0 }))
				},
				_: () => { },
			})
			return
		}

		// only positive integers
		if (/^\d+$/.test(value)) {
			df.IfLet().Relative(({ unit }) => {
				onChange(
					dateFilter.Relative({
						unit,
						count: value.Parse<number>().Expect("invalid int"),
					})
				)
			})
		}
	}

	// Prepare data for the dropdown - memoized to prevent recalculations
	const dropdownData = useMemo(() => {
		const options: dropdownOption[] = []

		// Add relative time options
		relativeUnits.Range((i: number, unit: string) => {
			options.Append({
				id: unit,
				name: pluralize(
					unit,
					df
						.IfLet()
						.Relative(({ count }) => count)
						.UnwrapOr(1)
				),
				type: "relative",
			})
		})

		// Add All Time if not disabled
		if (!disableAllTime) {
			options.Append({
				id: date.AllTime,
				name: "All Time",
				type: "allTime",
			})
		}

		// Add Custom if not disabled
		if (!disableCustom) {
			options.Append({
				id: date.Custom,
				name: "Custom",
				type: "custom",
			})
		}

		return options
	}, [relativeUnits, df, disableAllTime, disableCustom])

	// Get current value for the dropdown
	const currentDropdownValue = useMemo(() => {
		const currentId = df.Match({
			Relative: ({ unit }) => unit.Tag,
			Range: () => "Custom",
			SingleDate: () => "Custom",
			_: () => "AllTime",
		})

		return dropdownData.Find((item) => item.id === currentId).UnwrapOr(null as any)
	}, [df, dropdownData])

	// Handle dropdown change - stable reference
	const handleDropdownChange = useCallback(
		(selected: dropdownOption | null) => {
			if (!selected) return // No selection

			const value = selected.id

			if (value === date.Custom) {
				// Switch to range, and open the calendar
				onChange(dateFilter.Range([util.Moment(), util.Moment()]))
				setCalendarOpen(true)
				return
			}

			if (timePeriod.IsTimePeriodTag(value)) {
				const newFilter = dateFilter.Relative({
					unit: value.Match({
						Day: () => timePeriod.Day(),
						Week: () => timePeriod.Week(),
						Month: () => timePeriod.Month(),
						Year: () => timePeriod.Year(),
					}),
					count: df.Match({
						Relative: ({ count }) => count,
						_: () => 1,
					}),
				})
				onChange(newFilter)
			} else if (timePeriod.IsAllTime(value)) {
				onChange(dateFilter.AllTime())
			}

			setCalendarOpen(false)
		},
		[df, onChange, setCalendarOpen]
	)

	// Stable clear handler
	const handleClear = useCallback(() => {
		onChange(dateFilter.AllTime())
	}, [onChange])

	// Material You theme colors - memoized
	const colors = useMemo<ColorTheme>(
		() => ({
			border: alpha(materialColors.outline, 0.5),
			borderHover: materialColors.outline,
			borderFocus: materialColors.primary,
			background: alpha(
				materialColors.surfaceBright,
				materialColors.isDark ? 0.6 : 0.3
			),
			calendarBackground: materialColors.surfaceContainerHigh,
			shadow: materialColors.elevation?.elevation3 || "",
			text: materialColors.onSurface,
			secondaryText: materialColors.onSurfaceVariant,
			activeText: materialColors.primary,
			disabledText: alpha(materialColors.onSurfaceVariant, 0.6),
			error: materialColors.error,
			ripple: alpha(materialColors.primary, 0.1),
		}),
		[materialColors]
	)

	// CONSISTENT container styles for both modes
	const containerStyles = useMemo(
		() => ({
			width: "100%",
			minWidth: "100%",
			maxWidth: "100%",
			backgroundColor: colors.background,
			borderRadius: 15,
			paddingLeft: "24px",
			paddingRight: "16px",
			position: "relative" as const,
			zIndex: 1,
			border: `1px solid ${colors.border}`,
			transition: "all 0.3s cubic-bezier(0.4, 0.0, 0.2, 1)",
			backdropFilter: "blur(10px)",
			WebkitBackdropFilter: "blur(10px)",
			display: "flex",
			alignItems: "center",
			overflow: "hidden" as const,
			flexWrap: "nowrap" as const,
			justifyContent: "flex-start" as const,
			boxShadow: `0 1px 3px 0 ${alpha(materialColors.onSurface, 0.04)}, 0 1px 2px -1px ${alpha(materialColors.onSurface, 0.08)}`,
		}),
		[colors, materialColors, df, label]
	)

	// Memoized label component
	/* eslint-disable-next-line */
	const LabelComponent = useMemo(() => {
		if (!label || df.Is("AllTime")) return null

		return (
			<Text
				style={{
					position: "absolute",
					top: "-0.5rem",
					left: "1rem",
					fontSize: "0.75rem",
					fontWeight: 500,
					backgroundColor: alpha(
						materialColors.surfaceContainerHigh,
						materialColors.isDark ? 0.9 : 0.95
					),
					padding: "0 0.25rem",
					borderRadius: "0.25rem",
					color: materialColors.primary,
					pointerEvents: "none",
					transformOrigin: "left top",
					transition: "all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)",
					zIndex: 10,
					boxShadow: `0px 0px 4px ${alpha(materialColors.surfaceContainerHigh, 0.8)}`,
				}}
			>
				{label}
			</Text>
		)
	}, [label, df, materialColors])

	// Memoized DateRangeDisplay component
	/* eslint-disable-next-line */
	const DateRangeDisplay = useMemo(() => {
		if (df.Is("AllTime")) return null

		const isClickable = !df.IsCustom() // Relative dates can click to switch to custom

		return (
			<StyledBox
				component={motion.div}
				whileHover={{
					scale: isClickable ? 1.02 : 1,
					transition: { duration: 0.15, ease: [0.4, 0.0, 0.2, 1] },
				}}
				whileTap={isClickable ? { scale: 0.98 } : {}}
				onClick={
					isClickable
						? () => {
							dropdownData
								.Find((opt) => opt.id === date.Custom)
								.IfSome(handleDropdownChange)
						}
						: undefined
				}
				sx={{
					display: "flex",
					alignItems: "center",
					gap: "10px",
					color: colors.secondaryText,
					cursor: isClickable ? "pointer" : "default",
					userSelect: "none",
					padding: "8px 16px",
					borderRadius: 20,
					transition: "all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)",
					flexShrink: 1,
					marginLeft: "8px",
					backgroundColor: isClickable
						? materialColors.surfaceBright
						: alpha(materialColors.onSurface, 0.04),
					minWidth: 0,
					flex: "1 1 auto",
					border: `1px solid ${isClickable
						? alpha(materialColors.primary, 0.2)
						: alpha(materialColors.outline, 0.12)
						}`,
					position: "relative",
					overflow: "hidden",
					...(isClickable && {
						"&::before": {
							content: '""',
							position: "absolute",
							top: 0,
							left: 0,
							right: 0,
							bottom: 0,
							background: `radial-gradient(circle at center, ${alpha(
								materialColors.primary,
								0.15
							)} 0%, transparent 70%)`,
							opacity: 0,
							transition: "opacity 0.3s cubic-bezier(0.4, 0.0, 0.2, 1)",
						},
						"&:hover": {
							color: materialColors.primary,
							backgroundColor: alpha(materialColors.surfaceBright, 0.12),
							borderColor: alpha(materialColors.primary, 0.3),
							transform: "translateY(-1px)",
							boxShadow: `0 4px 8px ${alpha(materialColors.primary, 0.15)}, 0 1px 3px ${alpha(
								materialColors.primary,
								0.08
							)}`,
						},
						"&:hover::before": {
							opacity: 1,
						},
						"&:active": {
							transform: "translateY(0)",
							boxShadow: `0 1px 2px ${alpha(materialColors.primary, 0.1)}`,
						},
					}),
				}}
			>
				{/* Calendar icon with enhanced styling */}
				<CalendarOutlined
					style={{
						fontSize: 18,
						color: isClickable ? materialColors.primary : colors.secondaryText,
						flexShrink: 0,
						transition: "all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)",
						...(isClickable && {
							filter: `drop-shadow(0 0 2px ${alpha(materialColors.primary, 0.3)})`,
						}),
					}}
				/>

				<Text
					style={{
						fontSize: "0.8125rem",
						letterSpacing: "0.01em",
						fontWeight: 500,
						whiteSpace: "nowrap",
						color: isClickable ? materialColors.primary : colors.secondaryText,
						overflow: "hidden",
						textOverflow: "ellipsis",
						lineHeight: "20px",
						minWidth: 0,
						flex: "1 1 auto",
						transition: "color 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)",
					}}
				>
					{df.Is("SingleDate") ? df.ToStr() : df.GetDateRange().ToStr(format)}
				</Text>

				{/* Add a subtle arrow indicator for clickable state */}
				{isClickable && (
					<motion.div
						initial={{ opacity: 0, x: -4 }}
						animate={{ opacity: 1, x: 0 }}
						style={{
							fontSize: "12px",
							color: materialColors.primary,
							marginLeft: "4px",
							flexShrink: 0,
							display: "flex",
							alignItems: "center",
						}}
					>
						→
					</motion.div>
				)}
			</StyledBox>
		)
	}, [df, colors, materialColors, format, handleDropdownChange, dropdownData])

	// Memoized ClearButton component
	/* eslint-disable-next-line */
	const ClearButton = useMemo(() => {
		if (df.Is("AllTime") || df.IsCustom()) return null

		return (
			<Button
				type="text"
				onClick={handleClear}
				size="small"
				icon={<CloseOutlined style={{ fontSize: 18, color: colors.secondaryText }} />}
				style={clearButtonStyle}
				onMouseEnter={(e) => {
					e.currentTarget.style.backgroundColor = alpha(materialColors.onSurface, 0.08)
					const icon = e.currentTarget.querySelector(".anticon-close")
					if (icon) (icon as HTMLElement).style.color = colors.text
				}}
				onMouseLeave={(e) => {
					e.currentTarget.style.backgroundColor = "transparent"
					const icon = e.currentTarget.querySelector(".anticon-close")
					if (icon) (icon as HTMLElement).style.color = colors.secondaryText
				}}
				aria-label="Clear filter"
			/>
		)
	}, [df, colors, handleClear, materialColors])

	// UNIFIED component layout - always use the same container structure
	return (
		<StyledBox
			sx={{
				position: "relative",
				width: "100%", // Always full width of parent
				maxWidth: "100%", // Prevent overflow
			}}
			ref={containerRef}
		>
			{df.Is("Relative") ? (
				// For "Last X Days/Weeks/etc" UI
				<StyledBox>
					<StyledBox sx={{ position: "relative", width: "100%" }}>
						<StyledStack
							component={motion.div}
							initial={{ opacity: 0, y: 5 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.3, ease: [0.0, 0.0, 0.2, 1.0] }}
							direction="horizontal"
							spacing={0}
							sx={{
								...containerStyles,
								gap: "4px",
								cursor: "default",
							}}
							onMouseEnter={(e: any) => {
								e.currentTarget.style.borderColor = colors.borderHover
								e.currentTarget.style.backgroundColor = alpha(
									materialColors.surfaceBright,
									materialColors.isDark ? 0.8 : 0.5
								)
								e.currentTarget.style.boxShadow = `0 4px 8px 0 ${alpha(materialColors.onSurface, 0.08)}, 0 2px 4px -1px ${alpha(materialColors.onSurface, 0.12)}`
							}}
							onMouseLeave={(e: any) => {
								e.currentTarget.style.borderColor = colors.border
								e.currentTarget.style.backgroundColor = colors.background
								e.currentTarget.style.boxShadow = `0 1px 3px 0 ${alpha(materialColors.onSurface, 0.04)}, 0 1px 2px -1px ${alpha(materialColors.onSurface, 0.08)}`
							}}
						>
							<Text
								style={{
									color: colors.secondaryText,
									fontSize: "0.875rem",
									userSelect: "none",
									whiteSpace: "nowrap",
									letterSpacing: "0.01em",
									flexShrink: 0,
									fontWeight: 500,
								}}
							>
								Last
							</Text>

							<MemoizedTextField
								value={textFieldValue}
								handleTextChange={handleTextChange}
								inputProps={inputProps}
								colors={colors}
							/>

							<MemoizedDropdownContainer
								dropdownData={dropdownData}
								currentDropdownValue={currentDropdownValue}
								handleDropdownChange={handleDropdownChange}
							/>

							{ClearButton}
							{DateRangeDisplay}
						</StyledStack>
					</StyledBox>
				</StyledBox>
			) : (
				// For all time and custom modes
				<StyledBox>
					{df.Is("AllTime") ? (
						<StyledStack
							component={motion.div}
							initial={{ opacity: 0, y: 5 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.3, ease: [0.0, 0.0, 0.2, 1.0] }}
							direction="horizontal"
							spacing={0}
							sx={{
								width: "100%",
							}}
						>
							{/* Dropdown with better sizing - fixed for timeperiod specifically */}
							<Dropdown
								data={dropdownData}
								value={currentDropdownValue}
								onChange={handleDropdownChange}
								clearable={false}
								getOptionLabel={(option: dropdownOption) => option.name}
								isOptionEqualToValue={(
									option: dropdownOption,
									value: dropdownOption
								) => option.id === value.id}
								multiple={false}
								textFieldProps={{
									style: {
										fontSize: "0.875rem",
										fontWeight: 500,
										letterSpacing: "0.01em",
									},
								}}
								sx={dropdownSx}
							/>
						</StyledStack>
					) : (
						<StyledBox sx={{ position: "relative", width: "100%" }}>

							<StyledStack
								component={motion.div}
								initial={{ opacity: 0, y: 5 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.3, ease: [0.0, 0.0, 0.2, 1.0] }}
								direction="horizontal"
								spacing={0}
								sx={{
									...containerStyles,
									gap: "8px",
								}}
								onMouseEnter={(e: any) => {
									e.currentTarget.style.borderColor = colors.borderHover
									e.currentTarget.style.backgroundColor = alpha(materialColors.surfaceBright, 0.08)
									e.currentTarget.style.boxShadow = `0 4px 8px 0 ${alpha(materialColors.onSurface, 0.08)}, 0 2px 4px -1px ${alpha(materialColors.onSurface, 0.12)}`
								}}
								onMouseLeave={(e: any) => {
									e.currentTarget.style.borderColor = colors.border
									e.currentTarget.style.backgroundColor = colors.background
									e.currentTarget.style.boxShadow = `0 1px 3px 0 ${alpha(materialColors.onSurface, 0.04)}, 0 1px 2px -1px ${alpha(materialColors.onSurface, 0.08)}`
								}}
							>
								<MemoizedDropdownContainer
									dropdownData={dropdownData}
									currentDropdownValue={currentDropdownValue}
									handleDropdownChange={handleDropdownChange}
								/>
								{ClearButton}

								<MemoizedRangePicker
									datePickerValue={df.GetDatePickerValue()}
									calendarOpen={calendarOpen}
									isCustom={df.IsCustom()}
									setCalendarOpen={setCalendarOpen}
									onOpenCalendar={onOpenCalendar}
									onCloseCalendar={onCloseCalendar}
									onChange={onChange}
									materialColors={materialColors}
									format={format}
								/>
							</StyledStack>
						</StyledBox>
					)}
				</StyledBox>
			)}
		</StyledBox>
	)
}

// Use memo with custom comparison function
export const DateFilterComponent = memo(dateFilterComponent)
